import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { _Api } from "../common/api/_call";
import { error, success } from "../common/helpers/toastify";

const POLL_INTERVAL_MS = 5000;
const COUNTDOWN_TICK_MS = 1000;
const DEFAULT_COUNTDOWN_SEC = 1800;

/** Cregis Payment Engine order statuses — webhook / order-info is source of truth */
const STATUS = {
  NEW: "new",
  PAID: "paid",
  PAID_OVER: "paid_over",
  PAID_PARTIAL: "paid_partial",
  EXPIRED: "expired",
  CANCELED: "canceled",
};

const resolveStatus = (data = {}) =>
  String(
    data?.status ||
      data?.payment_status ||
      data?.order_status ||
      STATUS.NEW
  ).toLowerCase();

const normalizePaymentInfo = (data = {}) => {
  const candidates = [
    data?.payment_info,
    data?.paymentInfo,
    data?.cregis_payment_info,
    data?.pay_data?.cregis_payment_info,
    data?.pay_data?.cregis?.payment_info,
    data?.cregis?.payment_info,
  ];

  for (const raw of candidates) {
    let info = raw;
    if (typeof info === "string") {
      try {
        info = JSON.parse(info);
      } catch {
        continue;
      }
    }
    if (Array.isArray(info) && info.length) {
      return info.filter((item) => item?.payment_address);
    }
  }

  return [];
};

const buildQrImageUrl = (text) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
    text || ""
  )}`;

/** Only trust backend-verified paid states (webhook / query order) */
const isPaymentSuccess = (data = {}) => {
  if (
    data?.payment_success === true ||
    data?.is_paid === true ||
    data?.can_complete_sale === true
  ) {
    return true;
  }
  const status = resolveStatus(data);
  return status === STATUS.PAID || status === STATUS.PAID_OVER;
};

const isTerminalFailure = (data = {}) => {
  const status = resolveStatus(data);
  return status === STATUS.EXPIRED || status === STATUS.CANCELED;
};

const formatCountdown = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const statusLabel = (status) => {
  const s = String(status || STATUS.NEW).toLowerCase();
  if (s === STATUS.PAID) return "Paid";
  if (s === STATUS.PAID_OVER) return "Overpaid";
  if (s === STATUS.PAID_PARTIAL) return "Partial payment";
  if (s === STATUS.EXPIRED) return "Expired";
  if (s === STATUS.CANCELED) return "Canceled";
  return "Awaiting payment";
};

const getPaymentOptionKey = (item = {}, index = 0) =>
  item?.token_name || item?.blockchain || `option-${index}`;

const getCryptoKey = (item = {}, index = 0) =>
  String(item?.receive_currency || item?.token_symbol || `crypto-${index}`).toUpperCase();

const resolveCryptoLabel = (item = {}) =>
  String(item?.receive_currency || item?.token_symbol || "").trim();

/** Subtitle only when Cregis API provides extra display text (no hardcoded coin names). */
const resolveCryptoSubtitle = (item = {}) => {
  const label = resolveCryptoLabel(item);
  const tokenSymbol = String(item?.token_symbol || "").trim();
  const tokenName = String(item?.token_name || "").trim();

  if (tokenSymbol && label && tokenSymbol.toUpperCase() !== label.toUpperCase()) {
    return tokenSymbol;
  }
  if (tokenName && !tokenName.includes("-") && tokenName.toUpperCase() !== label.toUpperCase()) {
    return tokenName;
  }
  return "";
};

/** Network label built from Cregis payment_info fields only. */
const formatNetworkLabel = (item = {}) => {
  const tokenName = String(item?.token_name || "").trim();
  const blockchain = String(item?.blockchain || "").trim();

  if (tokenName && blockchain && tokenName.includes("-")) {
    const networkPart = tokenName.split("-").slice(1).join("-");
    if (networkPart) return `${blockchain} (${networkPart})`;
  }

  return tokenName || blockchain || "Network";
};

const groupPaymentOptions = (options = []) => {
  const cryptoMap = new Map();

  options.forEach((item, index) => {
    const cryptoKey = getCryptoKey(item, index);
    if (!cryptoMap.has(cryptoKey)) {
      cryptoMap.set(cryptoKey, {
        key: cryptoKey,
        label: resolveCryptoLabel(item) || cryptoKey,
        subtitle: resolveCryptoSubtitle(item),
        logo: item?.asset_logo || item?.logo_url || "",
        amount: item?.receive_amount || "",
        networks: [],
      });
    }

    const group = cryptoMap.get(cryptoKey);
    group.networks.push({
      ...item,
      optionKey: getPaymentOptionKey(item, index),
      networkLabel: formatNetworkLabel(item),
    });

    if (!group.logo && (item?.asset_logo || item?.logo_url)) {
      group.logo = item.asset_logo || item.logo_url;
    }
    if (!group.amount && item?.receive_amount) {
      group.amount = item.receive_amount;
    }
    if (!group.subtitle) {
      group.subtitle = resolveCryptoSubtitle(item);
    }
  });

  return Array.from(cryptoMap.values());
};

const CregisDropdown = ({
  id,
  label,
  disabled,
  isOpen,
  onToggle,
  onClose,
  placeholder,
  selectedContent,
  children,
}) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onClose]);

  return (
    <div
      ref={rootRef}
      className={`cregis-payment__dropdown${disabled ? " is-disabled" : ""}${
        isOpen ? " is-open" : ""
      }`}
    >
      <span className="cregis-payment__field-label">{label}</span>
      <button
        id={id}
        type="button"
        className="cregis-payment__dropdown-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <div className="cregis-payment__dropdown-trigger-content">
          {selectedContent || (
            <span className="cregis-payment__dropdown-placeholder">{placeholder}</span>
          )}
        </div>
        <span className="cregis-payment__dropdown-chevron" aria-hidden="true" />
      </button>
      {isOpen ? (
        <ul className="cregis-payment__dropdown-menu" role="listbox" aria-labelledby={id}>
          {children}
        </ul>
      ) : null}
    </div>
  );
};

const CregisDropdownItem = ({ icon, title, subtitle, amount, onSelect, isActive }) => (
  <li role="option" aria-selected={isActive}>
    <button
      type="button"
      className={`cregis-payment__dropdown-item${isActive ? " is-active" : ""}`}
      onClick={onSelect}
    >
      {icon ? (
        <img src={icon} alt="" className="cregis-payment__dropdown-item-icon" />
      ) : (
        <span className="cregis-payment__dropdown-item-icon cregis-payment__dropdown-item-icon--empty" />
      )}
      <span className="cregis-payment__dropdown-item-text">
        <strong>{title}</strong>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      {amount ? <span className="cregis-payment__dropdown-item-amount">{amount}</span> : null}
    </button>
  </li>
);

/**
 * POS Cregis crypto payment — inline checkout using payment_info from API.
 * Cregis hosted page cannot be iframe-embedded; we render crypto + QR in-panel.
 */
const CregisPayment = ({
  customer_id,
  amount,
  paymentDetails,
  onPaymentSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [message, setMessage] = useState("");
  const [cregisId, setCregisId] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [countdown, setCountdown] = useState(DEFAULT_COUNTDOWN_SEC);
  const [orderStatus, setOrderStatus] = useState(STATUS.NEW);
  const [orderAmount, setOrderAmount] = useState("");
  const [orderCurrency, setOrderCurrency] = useState("USD");
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState("");
  const [selectedNetworkKey, setSelectedNetworkKey] = useState("");
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [cryptoDropdownOpen, setCryptoDropdownOpen] = useState(false);
  const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);

  const cryptoGroups = useMemo(
    () => groupPaymentOptions(paymentOptions),
    [paymentOptions]
  );

  const selectedCryptoGroup = useMemo(
    () => cryptoGroups.find((group) => group.key === selectedCrypto) || null,
    [cryptoGroups, selectedCrypto]
  );

  const networkOptions = selectedCryptoGroup?.networks || [];

  const selectedPayment =
    networkOptions.find((item) => item.optionKey === selectedNetworkKey) || null;

  const transactionIdRef = useRef("");
  const cregisIdRef = useRef("");
  const pollCountRef = useRef(0);
  const pollTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const completedRef = useRef(false);
  const selectedPaymentMetaRef = useRef({});

  useEffect(() => {
    if (!selectedPayment) {
      selectedPaymentMetaRef.current = {};
      return;
    }
    selectedPaymentMetaRef.current = {
      cryptoCurrency:
        selectedPayment?.receive_currency ||
        selectedCryptoGroup?.label ||
        "",
      cryptocurrency:
        selectedPayment?.receive_currency ||
        selectedCryptoGroup?.label ||
        "",
      crypto_currency:
        selectedPayment?.receive_currency ||
        selectedCryptoGroup?.label ||
        "",
      network:
        selectedPayment?.networkLabel ||
        selectedPayment?.blockchain ||
        "",
      blockchain: selectedPayment?.blockchain || "",
      token_name: selectedPayment?.token_name || "",
      token_symbol: selectedPayment?.token_symbol || "",
      receive_currency: selectedPayment?.receive_currency || "",
      receive_amount: selectedPayment?.receive_amount || "",
    };
  }, [selectedPayment, selectedCryptoGroup?.label]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setPolling(false);
  }, []);

  const applyOrderMeta = useCallback((data = {}) => {
    if (data?.cregis_id) {
      cregisIdRef.current = data.cregis_id;
      setCregisId(data.cregis_id);
    }

    const info = normalizePaymentInfo(data);
    if (info.length) {
      setPaymentOptions(info);
    }

    if (data?.order_amount) {
      setOrderAmount(String(data.order_amount));
    }
    if (data?.order_currency) {
      setOrderCurrency(String(data.order_currency));
    }
  }, []);

  const loadPaymentInfoFromQuery = useCallback(async () => {
    if (!cregisIdRef.current) return [];

    try {
      const queryRes = await _Api(
        {
          cregis_id: cregisIdRef.current,
          transactionId: transactionIdRef.current,
          payment_slag: paymentDetails?.payment_slag || "cregis-payment",
        },
        `api/v1/POS/PaymentQRcode/cregis/queryOrder`
      );

      if (queryRes?.code === 200) {
        applyOrderMeta(queryRes.data || {});
        return normalizePaymentInfo(queryRes.data || {});
      }
    } catch (queryErr) {
      console.warn("Cregis queryOrder load payment_info failed", queryErr);
    }

    return [];
  }, [applyOrderMeta, paymentDetails?.payment_slag]);

  const completePayment = useCallback(
    (data = {}) => {
      if (completedRef.current) return;
      completedRef.current = true;
      stopPolling();

      const paidId =
        data?.cregis_id ||
        data?.transactionID ||
        data?.payment_intent_id ||
        cregisIdRef.current ||
        transactionIdRef.current;

      const cryptoMeta = selectedPaymentMetaRef.current || {};

      setOrderStatus(resolveStatus(data) || STATUS.PAID);
      setMessage(t("Payment verified by Cregis. Completing order..."));
      success(t("Payment successful!"));
      if (typeof onPaymentSuccess === "function") {
        onPaymentSuccess(paidId, true, amount, cryptoMeta);
      }
    },
    [amount, onPaymentSuccess, stopPolling, t]
  );

  const fetchQueryOrderStatus = useCallback(async () => {
    if (!cregisIdRef.current || completedRef.current) return null;

    try {
      const queryRes = await _Api(
        {
          cregis_id: cregisIdRef.current,
          transactionId: transactionIdRef.current,
          payment_slag: paymentDetails?.payment_slag || "cregis-payment",
        },
        `api/v1/POS/PaymentQRcode/cregis/queryOrder`
      );

      if (queryRes?.code !== 200) return null;

      const queryData = queryRes?.data || {};
      applyOrderMeta(queryData);
      if (queryData?.status) {
        setOrderStatus(resolveStatus(queryData));
      }
      if (isPaymentSuccess(queryData)) {
        completePayment(queryData);
      }
      return queryData;
    } catch (queryErr) {
      console.warn("Cregis queryOrder status check failed", queryErr);
      return null;
    }
  }, [applyOrderMeta, completePayment, paymentDetails?.payment_slag]);

  const checkPaymentStatus = useCallback(async () => {
    const txId = transactionIdRef.current;
    const cregisId = cregisIdRef.current;
    if ((!txId && !cregisId) || completedRef.current) return;

    try {
      const response = await _Api(
        {
          transactionId: txId || undefined,
          payment_slag: paymentDetails?.payment_slag || "cregis-payment",
          cregis_id: cregisId || undefined,
        },
        `api/v1/POS/PaymentQRcode/getPaymentStatus`
      );

      if (response?.code === 404 && cregisId) {
        await fetchQueryOrderStatus();
        return;
      }

      if (response?.code !== 200) {
        setMessage(response?.message || t("Unable to check payment status"));
        if (cregisId) {
          await fetchQueryOrderStatus();
        }
        return;
      }

      const data = response?.data || {};
      const status = resolveStatus(data);
      setOrderStatus(status);
      applyOrderMeta(data);

      if (isPaymentSuccess(data)) {
        completePayment(data);
        return;
      }

      if (isTerminalFailure(data)) {
        stopPolling();
        const failMsg =
          status === STATUS.EXPIRED
            ? t("Payment expired. Please create a new order.")
            : t("Payment canceled.");
        setMessage(failMsg);
        error(failMsg);
        return;
      }

      if (status === STATUS.PAID_PARTIAL) {
        setMessage(t("Partial payment received. Please complete the remaining amount."));
      } else if (showPaymentDetails) {
        setMessage(t("Waiting for payment confirmation..."));
      }

      pollCountRef.current += 1;
      if (cregisId) {
        await fetchQueryOrderStatus();
      }
    } catch (err) {
      console.error("Cregis getPaymentStatus error:", err);
      if (cregisIdRef.current) {
        await fetchQueryOrderStatus();
      }
    }
  }, [
    applyOrderMeta,
    completePayment,
    fetchQueryOrderStatus,
    paymentDetails?.payment_slag,
    showPaymentDetails,
    stopPolling,
    t,
  ]);

  const startPolling = useCallback(() => {
    stopPolling();
    setPolling(true);
    pollCountRef.current = 0;
    checkPaymentStatus();

    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopPolling();
          setMessage(t("Payment timeout. Please try again."));
          error(t("Payment timeout. Please try again."));
          return 0;
        }
        return prev - 1;
      });
    }, COUNTDOWN_TICK_MS);

    pollTimerRef.current = setInterval(() => {
      checkPaymentStatus();
    }, POLL_INTERVAL_MS);
  }, [checkPaymentStatus, stopPolling, t]);

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      success(t("Copied"));
    } catch (err) {
      error(t("Copy failed"));
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedCrypto) {
      error(t("Please select cryptocurrency"));
      return;
    }
    if (!selectedPayment) {
      error(t("Please select network"));
      return;
    }

    const cryptoCurrency =
      selectedPayment?.receive_currency ||
      selectedCryptoGroup?.label ||
      "";
    const network =
      selectedPayment?.networkLabel || selectedPayment?.blockchain || "";

    // Persist selection so Payment Report can show crypto + network under Transaction ID
    try {
      await _Api(
        {
          cregis_id: cregisIdRef.current || undefined,
          transactionId: transactionIdRef.current || undefined,
          payment_slag: paymentDetails?.payment_slag || "cregis-payment",
          cryptocurrency: cryptoCurrency,
          crypto_currency: cryptoCurrency,
          cryptoCurrency,
          network,
          blockchain: selectedPayment?.blockchain || "",
          token_name: selectedPayment?.token_name || "",
          token_symbol: selectedPayment?.token_symbol || "",
          receive_currency: selectedPayment?.receive_currency || "",
          receive_amount: selectedPayment?.receive_amount || "",
          payment_address: selectedPayment?.payment_address || "",
        },
        `api/v1/POS/PaymentQRcode/cregis/saveSelection`
      );
    } catch (saveErr) {
      console.warn("Cregis saveSelection failed", saveErr);
    }

    setShowPaymentDetails(true);
    setMessage(t("Send the exact amount to the address below. Waiting for confirmation..."));
  };

  const handleCryptoChange = (cryptoKey) => {
    setSelectedCrypto(cryptoKey);
    setSelectedNetworkKey("");
    setShowPaymentDetails(false);
    setNetworkDropdownOpen(false);
  };

  const closeAllDropdowns = () => {
    setCryptoDropdownOpen(false);
    setNetworkDropdownOpen(false);
  };

  const createCregisOrder = async () => {
    if (!customer_id) {
      error(t("Please select customer"));
      return;
    }
    if (!paymentDetails?.PaymentMethod && !paymentDetails?.payment_method_id) {
      error(t("Please select payment method"));
      return;
    }
    if (!amount || Number(amount) <= 0) {
      error(t("Amount is not Valid"));
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      completedRef.current = false;
      stopPolling();
      setShowPaymentDetails(false);
      setSelectedCrypto("");
      setSelectedNetworkKey("");
      setCryptoDropdownOpen(false);
      setNetworkDropdownOpen(false);
      setPaymentOptions([]);

      const postdata = {
        payment_id:
          paymentDetails?.PaymentMethod || paymentDetails?.payment_method_id,
        amount: Number(amount),
        customerId: customer_id,
        payment_slag: paymentDetails?.payment_slag || "cregis-payment",
      };

      const response = await _Api(
        postdata,
        `api/v1/POS/PaymentQRcode/generateQRcode`
      );

      if (response?.code !== 200) {
        setMessage(response?.message || t("Failed to create crypto payment"));
        error(response?.message || t("Failed to create crypto payment"));
        return;
      }

      const data = response?.data || {};
      const nextTxId =
        data?.client_reference_id || data?.order_id || data?.transactionId || "";
      const nextCregisId = data?.cregis_id || "";

      if (!nextTxId && !nextCregisId) {
        error(t("Failed to create crypto payment"));
        setMessage(t("Failed to create crypto payment"));
        return;
      }

      transactionIdRef.current = nextTxId;
      cregisIdRef.current = nextCregisId;
      setTransactionId(nextTxId);
      setCregisId(nextCregisId);
      setOrderStatus(STATUS.NEW);
      setOrderAmount(String(data?.order_amount || amount));
      setOrderCurrency(String(data?.order_currency || "USD"));

      if (data?.expire_time) {
        const remainingSec = Math.max(
          60,
          Math.floor((Number(data.expire_time) - Date.now()) / 1000)
        );
        setCountdown(remainingSec);
      } else if (data?.valid_time) {
        setCountdown(Number(data.valid_time) * 60);
      } else {
        setCountdown(DEFAULT_COUNTDOWN_SEC);
      }

      applyOrderMeta(data);

      let options = normalizePaymentInfo(data);
      if (!options.length && nextCregisId) {
        options = await loadPaymentInfoFromQuery();
      }

      if (!options.length) {
        console.warn("Cregis payment_info missing from API response", data);
        setMessage(
          t(
            "Crypto options not returned by API yet. Restart backend and try again, or contact support."
          )
        );
        error(t("Payment options not available from Cregis."));
        return;
      }

      setPaymentOptions(options);
      // setMessage(t("Select cryptocurrency and network."));
      startPolling();
    } catch (err) {
      console.error("Cregis create order error:", err);
      error(t("Failed to create crypto payment"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const displayAmount = orderAmount || String(amount || "");
  const displayCurrency = orderCurrency || "USD";

  return (
    <div className="cregis-payment">
      {!transactionId ? (
        <button
          type="button"
          className="cregis-payment__start-btn"
          disabled={loading}
          onClick={createCregisOrder}
        >
          {loading ? t("Creating payment...") : t("Pay with Crypto")}
        </button>
      ) : (
        <div className="cregis-payment__panel">
          {/* <div className="cregis-payment__meta">
            <div>
              <span className="cregis-payment__label">{t("Status")}</span>
              <strong
                className={`cregis-payment__status cregis-payment__status--${orderStatus}`}
              >
                {t(statusLabel(orderStatus))}
              </strong>
            </div>
            <div>
              <span className="cregis-payment__label">{t("Time remaining")}</span>
              <strong>{formatCountdown(countdown)}</strong>
            </div>
          </div> */}

          <div className="cregis-payment__inline-card" style={{ marginBottom: "20px" }}>
            <div className="cregis-payment__inline-header">
              <span className="cregis-payment__inline-amount">
                {t("Pay")} {displayAmount} {displayCurrency}
              </span>
              <strong>{formatCountdown(countdown)}</strong>
            </div>

            {!showPaymentDetails ? (
              <>
                <CregisDropdown
                  id="cregis-crypto-select"
                  label={t("Cryptocurrency")}
                  placeholder={t("Select")}
                  isOpen={cryptoDropdownOpen}
                  onClose={closeAllDropdowns}
                  onToggle={() => {
                    setNetworkDropdownOpen(false);
                    setCryptoDropdownOpen((open) => !open);
                  }}
                  selectedContent={
                    selectedCryptoGroup ? (
                      <>
                        {selectedCryptoGroup.logo ? (
                          <img
                            src={selectedCryptoGroup.logo}
                            alt=""
                            className="cregis-payment__dropdown-item-icon"
                          />
                        ) : null}
                        <span className="cregis-payment__dropdown-item-text">
                          <strong>{selectedCryptoGroup.label}</strong>
                          {selectedCryptoGroup.subtitle ? (
                            <small>{selectedCryptoGroup.subtitle}</small>
                          ) : null}
                        </span>
                        {selectedCryptoGroup.amount ? (
                          <span className="cregis-payment__dropdown-item-amount">
                            {selectedCryptoGroup.amount}
                          </span>
                        ) : null}
                      </>
                    ) : null
                  }
                >
                  {cryptoGroups.map((group) => (
                    <CregisDropdownItem
                      key={group.key}
                      icon={group.logo}
                      title={group.label}
                      subtitle={group.subtitle}
                      amount={group.amount}
                      isActive={selectedCrypto === group.key}
                      onSelect={() => {
                        handleCryptoChange(group.key);
                        setCryptoDropdownOpen(false);
                      }}
                    />
                  ))}
                </CregisDropdown>

                <CregisDropdown
                  id="cregis-network-select"
                  label={t("Network")}
                  placeholder={t("Select")}
                  disabled={!selectedCrypto}
                  isOpen={networkDropdownOpen}
                  onClose={closeAllDropdowns}
                  onToggle={() => {
                    if (!selectedCrypto) return;
                    setCryptoDropdownOpen(false);
                    setNetworkDropdownOpen((open) => !open);
                  }}
                  selectedContent={
                    selectedPayment ? (
                      <>
                        {selectedPayment.logo_url ? (
                          <img
                            src={selectedPayment.logo_url}
                            alt=""
                            className="cregis-payment__dropdown-item-icon"
                          />
                        ) : null}
                        <span className="cregis-payment__dropdown-item-text">
                          <strong>{selectedPayment.networkLabel}</strong>
                        </span>
                      </>
                    ) : null
                  }
                >
                  {networkOptions.map((item) => (
                    <CregisDropdownItem
                      key={item.optionKey}
                      icon={item.logo_url}
                      title={item.networkLabel}
                      isActive={selectedNetworkKey === item.optionKey}
                      onSelect={() => {
                        setSelectedNetworkKey(item.optionKey);
                        setShowPaymentDetails(false);
                        setNetworkDropdownOpen(false);
                      }}
                    />
                  ))}
                </CregisDropdown>

                <button
                  type="button"
                  className="cregis-payment__start-btn"
                  disabled={!selectedCrypto || !selectedPayment}
                  onClick={handleConfirmPayment}
                >
                  {t("Pay")}
                </button>
              </>
            ) : (
              <div className="cregis-payment__pay-box">
                <p className="cregis-payment__section-title">
                  {selectedPayment?.networkLabel ||
                    selectedPayment?.token_name ||
                    t("Payment details")}
                </p>

                <div className="cregis-payment__qr-wrap">
                  <img
                    src={buildQrImageUrl(selectedPayment?.payment_address)}
                    alt={t("Payment QR code")}
                    className="cregis-payment__qr-image"
                  />
                </div>

                <div className="cregis-payment__row">
                  <span>{t("Amount")}</span>
                  <strong>
                    {selectedPayment?.receive_amount}{" "}
                    {selectedPayment?.receive_currency}
                  </strong>
                  <button
                    type="button"
                    className="cregis-payment__copy"
                    onClick={() =>
                      copyToClipboard(
                        `${selectedPayment?.receive_amount || ""} ${
                          selectedPayment?.receive_currency || ""
                        }`.trim()
                      )
                    }
                  >
                    {t("Copy")}
                  </button>
                </div>

                <div className="cregis-payment__row cregis-payment__row--address">
                  <span>{t("Address")}</span>
                  <strong>{selectedPayment?.payment_address}</strong>
                  <button
                    type="button"
                    className="cregis-payment__copy"
                    onClick={() => copyToClipboard(selectedPayment?.payment_address)}
                  >
                    {t("Copy")}
                  </button>
                </div>

                <div className="cregis-payment__row">
                  <span>{t("Network")}</span>
                  <strong>
                    {selectedPayment?.networkLabel || selectedPayment?.blockchain}
                  </strong>
                </div>

                <button
                  type="button"
                  className="cregis-payment__refresh-btn"
                  onClick={() => {
                    setShowPaymentDetails(false);
                    setSelectedCrypto("");
                    setSelectedNetworkKey("");
                  }}
                >
                  {t("Change cryptocurrency")}
                </button>
              </div>
            )}
          </div>

          {/* {(cregisId || transactionId) && (
            <div className="cregis-payment__ids">
              {cregisId ? (
                <p>
                  <span>{t("Cregis ID")}:</span> {cregisId}
                </p>
              ) : null}
              {transactionId ? (
                <p>
                  <span>{t("Order ref")}:</span> {transactionId}
                </p>
              ) : null}
            </div>
          )}

          <div className="cregis-payment__actions">
            <button
              type="button"
              className="cregis-payment__refresh-btn"
              onClick={checkPaymentStatus}
            >
              {t("Refresh status")}
            </button>
          </div>

          {message ? <p className="cregis-payment__message">{message}</p> : null}
          {polling ? (
            <p className="cregis-payment__hint">
              {t("Listening for Cregis webhook → backend → PAID...")}
            </p>
          ) : null} */}
        </div>
      )}
    </div>
  );
};

export default CregisPayment;
